import { useState } from "react";
import { Notify, toast } from "@/components/ui/notify";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLines } from "@/features/lines/hooks";
import { useProducts } from "@/features/products/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { UploadStep } from "./steps/UploadStep";
import { MappingStep } from "./steps/MappingStep";
import { ResultStep } from "./steps/ResultStep";
import { useImportWizard } from "../hooks/useImportWizard";

/**
 * Spec 082 — quatro passos: enviar → mapear → executar → resultado. A composição vive aqui; a
 * máquina de estados e as mutações vivem em `useImportWizard`.
 */
export function ImportWizard() {
  const { data: lines = [] } = useLines();
  const { data: products = [] } = useProducts();
  const [profileName, setProfileName] = useState("");
  const w = useImportWizard();

  async function onUpload() {
    if (!w.file) {
      toast.error("Selecione um arquivo.");
      return;
    }
    try {
      await w.upload();
      toast.success("Arquivo enviado. Conferindo a amostra…");
    } catch (err) {
      // `putFileToSignedUrl` lança um Error simples (não `ApiClientError`) com mensagem própria;
      // `getFriendlyErrorMessage` só extrai de `ApiClientError`, então o fallback repete o texto
      // dela em vez do genérico "Erro inesperado.".
      toast.error("Falha no upload.", {
        description: getFriendlyErrorMessage(
          err,
          "Não foi possível enviar o arquivo. Tente novamente."
        ),
      });
    }
  }

  async function onSaveMapping() {
    try {
      await w.saveMapping(profileName.trim() || undefined);
      toast.success("Mapeamento conferido. A amostra abaixo é o que vai entrar.");
    } catch (err) {
      toast.error("Mapeamento recusado.", { description: getFriendlyErrorMessage(err) });
    }
  }

  async function onExecute() {
    try {
      await w.saveMapping(profileName.trim() || undefined);
      await w.execute();
      toast.success("Importação enfileirada — acompanhando…");
    } catch (err) {
      toast.error("Falha ao executar.", { description: getFriendlyErrorMessage(err) });
    }
  }

  async function onRevert() {
    // Ação destrutiva: apaga execuções. `ConfirmDialog` com nome digitado, como manda o design
    // system para tudo que não tem volta.
    const ok = await Notify.confirm({
      variant: "danger",
      title: "Desfazer esta importação?",
      description:
        "As execuções que ela criou serão removidas. O que foi digitado na tela no mesmo período continua lá.",
      confirmLabel: "Desfazer importação",
      typeToConfirm: "desfazer",
    });
    if (!ok) return;
    try {
      await w.revert();
      toast.success("Importação desfeita.");
    } catch (err) {
      toast.error("Não foi possível desfazer.", { description: getFriendlyErrorMessage(err) });
    }
  }

  function reset() {
    setProfileName("");
    w.reset();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Importar histórico de produção</CardTitle>
        <p className="text-xs text-muted-foreground">
          Etapa {w.step} de 4 — enviar → mapear → executar → resultado.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {w.trackingError ? (
          <Notify.QueryError error={w.trackingError} onRetry={w.retryTracking} />
        ) : null}

        {!w.trackingError && w.step === 1 ? (
          <UploadStep
            lines={lines}
            lineId={w.lineId}
            setLineId={w.setLineId}
            file={w.file}
            setFile={w.setFile}
            isPending={w.isUploading}
            onSubmit={() => void onUpload()}
          />
        ) : null}

        {!w.trackingError && w.step === 2 && w.preview ? (
          <MappingStep
            preview={w.preview}
            draft={w.draft}
            setDraft={w.patchDraft}
            lines={lines}
            products={products}
            profileName={profileName}
            setProfileName={setProfileName}
            isPending={w.isMutating}
            onCancel={reset}
            onSaveMapping={() => void onSaveMapping()}
            onExecute={() => void onExecute()}
          />
        ) : null}

        {!w.trackingError && w.step === 3 ? (
          <div className="space-y-2">
            <p className="text-sm">Importação em andamento…</p>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full w-1/2 bg-primary motion-safe:animate-pulse" />
            </div>
          </div>
        ) : null}

        {!w.trackingError && w.step === 4 && w.imp ? (
          <ResultStep
            imp={w.imp}
            isReverting={w.isReverting}
            onRevert={() => void onRevert()}
            onReset={reset}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
